<script lang="ts" setup>
import {ExtendedProject, ProjectNavigationItem} from "@/types";
import ProjectSidebar from "@/Pages/projects/layouts/components/ProjectSidebar.vue";
import {useDisplay} from "vuetify";
import {usePage} from "@inertiajs/vue3";
import {computed} from "vue";

interface Props {
    project: ExtendedProject,
    withDescription?: boolean,
}

const props = withDefaults(defineProps<Props>(), {
    withDescription: false,
})

const { mdAndUp } = useDisplay()

const page = usePage()

const role = computed(() => {
    return page.props.role
})

const items: Array<ProjectNavigationItem> = [
    {
        route: 'projects.show',
        label: 'Transfers',
        icon: 'mdi-cloud-upload-outline',
        roles: ['owner', 'admin', 'dta', 'member', 'guest'],
        show: true,
    },
    {
        label: 'Access',
        roles: ['owner', 'admin'],
    },
    {
        route: 'projects.users.index',
        label: 'Collaborators',
        icon: 'mdi-account-outline',
        roles: ['owner', 'admin'],
        show: true,
    },
    {
        route: 'projects.groups.index',
        label: 'Groups',
        icon: 'mdi-account-multiple-outline',
        roles: ['owner', 'admin'],
        show: props.project.has_groups,
    },
    {
        route: 'projects.keys.index',
        label: 'SSH and PGP keys',
        icon: 'mdi-key-outline',
        roles: ['owner', 'admin'],
        show: true,
    },
    {
        label: 'Integrations',
        roles: ['owner', 'admin'],
    },
    {
        route: 'projects.tokens.index',
        label: 'Personal access tokens',
        icon: 'mdi-key-outline',
        roles: ['owner', 'admin'],
        show: true,
    },
    {
        route: 'projects.subscriptions.index',
        label: 'Email notifications',
        icon: 'mdi-email-outline',
        roles: ['owner', 'admin'],
        show: true,
    },
    {
        label: 'Settings',
        roles: ['owner'],
    },
    {
        route: 'projects.edit',
        label: 'General',
        icon: 'mdi-cog-outline',
        roles: ['owner'],
        show: true,
    },
]
</script>

<template>
    <div>
        <v-navigation-drawer
            class="pl-5 border-e-0 pt-5 pr-2"
            mobile-breakpoint="sm"
            :width="mdAndUp ? 320 : undefined"
        >
            <project-sidebar
                :project="props.project"
                :role="role"
                :items="items"
            />
        </v-navigation-drawer>
        <v-row class="fill-height mx-3 mx-md-0">
            <v-col
                cols="12"
                :md="withDescription ? 8: 10"
                order="2"
                order-md="1"
            >
                <project-sidebar
                    class="d-block d-sm-none"
                    :project="props.project"
                    :role="role"
                    :items="items"
                />
                <slot />
            </v-col>
            <v-col
                v-if="props.withDescription"
                cols="12"
                md="4"
                order="1"
                order-md="2"
            >
                <div class="font-weight-bold mb-5 mt-7">
                    About
                </div>
                <div>
                    {{ props.project.description }}
                </div>
                <v-list
                    density="compact"
                    :slim="true"
                    class="px-0"
                >
                    <v-list-item
                        density="compact"
                        :slim="true"
                        prepend-icon="mdi-account-outline"
                        class="px-0 text-body-2"
                    >
                        <div class="text-grey-darken-2">
                            <span class="font-weight-bold">{{ props.project.users_count }}</span>
                            collaborators
                        </div>
                    </v-list-item>
                    <v-list-item
                        density="compact"
                        :slim="true"
                        prepend-icon="mdi-cloud-upload-outline"
                        class="px-0 text-body-2"
                    >
                        <div class="text-grey-darken-2">
                            <span class="font-weight-bold">{{ props.project.uploads_count }}</span>
                            transfers
                        </div>
                    </v-list-item>
                </v-list>
            </v-col>
        </v-row>
    </div>
</template>

<style scoped>
</style>
